import { useMutation, useQuery } from "@apollo/client/react";
import {
  Badge,
  Card,
  Code,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconEdit, IconTrash } from "@tabler/icons-react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DetailPageSkeleton } from "../../../components/SkeletonLoader";
import { PageHeader } from "../../../components/PageHeader";
import { useConfirmModal } from "../../../components/ConfirmModal";
import { DELETE_PRESET_SCRIPT_MUTATION } from "../steps/rules/graphql/mutations";
import {
  PRESET_SCRIPTS_QUERY,
  PRESET_SCRIPT_QUERY,
} from "../steps/rules/graphql/queries";
import {
  ScriptParameterDefinition,
  ScriptParameterType,
  SimulationPanel,
} from "../steps/rules/components/SimulationPanel";
import {
  ScriptContextType,
  ValidationPanel,
} from "../steps/rules/components/ValidationPanel";
import {
  PresetParameterDraft,
  PresetParameterType,
} from "./PresetParametersEditor";

const PRESET_SCRIPTS_LIST_PATH = "/tournaments/preset-scripts/list";
const NOTIFICATION_ERROR_MAX_LENGTH = 200;
const SCRIPT_PREVIEW_MAX_HEIGHT = 600;
const SCRIPT_PREVIEW_FONT_SIZE = 13;

interface PresetScriptDetailData {
  presetScript: {
    id: string;
    name: string;
    description: string | null;
    script: string;
    parameters: PresetParameterDraft[] | null;
    createdAt: string;
    updatedAt: string;
  };
}

const PARAMETER_TYPE_COLORS: Record<PresetParameterType, string> = {
  [PresetParameterType.INT]: "cyan",
  [PresetParameterType.FLOAT]: "indigo",
  [PresetParameterType.STRING]: "green",
  [PresetParameterType.BOOLEAN]: "amber",
};

// PresetParameterType and ScriptParameterType are parallel enums with
// identical string values. Keep an explicit map so divergence produces a
// TypeScript error rather than a silent runtime mismatch.
const PRESET_TO_SCRIPT_PARAMETER_TYPE: Readonly<
  Record<PresetParameterType, ScriptParameterType>
> = {
  [PresetParameterType.INT]: ScriptParameterType.INT,
  [PresetParameterType.FLOAT]: ScriptParameterType.FLOAT,
  [PresetParameterType.STRING]: ScriptParameterType.STRING,
  [PresetParameterType.BOOLEAN]: ScriptParameterType.BOOLEAN,
};

const toScriptParameterDefinitions = (
  parameters: PresetParameterDraft[],
): ScriptParameterDefinition[] =>
  parameters.map((parameter) => ({
    name: parameter.name,
    type: PRESET_TO_SCRIPT_PARAMETER_TYPE[parameter.type],
    required: parameter.required,
    defaultValue: parameter.defaultValue,
    description: parameter.description,
  }));

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

export const PresetScriptDetail = () => {
  const navigate = useNavigate();
  const presetScriptId = useParams().presetScriptId;
  const { confirm, modal } = useConfirmModal();

  const { data, loading } = useQuery<PresetScriptDetailData>(
    PRESET_SCRIPT_QUERY,
    {
      variables: { id: presetScriptId ?? "" },
      skip: !presetScriptId,
    },
  );

  const [deletePresetScript] = useMutation<
    { deletePresetScript: boolean },
    { id: string }
  >(DELETE_PRESET_SCRIPT_MUTATION, {
    refetchQueries: [{ query: PRESET_SCRIPTS_QUERY }],
  });

  const parameters = data?.presetScript?.parameters ?? [];
  const scriptParametersSchema = useMemo(
    () => toScriptParameterDefinitions(parameters),
    [parameters],
  );

  if (loading) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title="Preset Script"
          showBackButton
          showBreadcrumbs={false}
        />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (!data?.presetScript) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title="Preset Script"
          showBackButton
          showBreadcrumbs={false}
        />
        <Text>Preset script not found.</Text>
      </div>
    );
  }

  const preset = data.presetScript;

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete Preset Script",
      message: `Are you sure you want to delete "${preset.name}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      type: "danger",
    });

    if (!confirmed || !presetScriptId) {
      return;
    }

    try {
      const result = await deletePresetScript({
        variables: { id: presetScriptId },
      });

      if (result.error) {
        throw result.error;
      }

      notifications.show({
        title: "Preset deleted",
        message: `Preset "${preset.name}" has been deleted`,
        color: "green",
        icon: <IconCheck size={16} />,
        autoClose: 3000,
      });

      navigate(PRESET_SCRIPTS_LIST_PATH);
    } catch (error) {
      notifications.show({
        title: "Delete failed",
        message:
          error instanceof Error &&
          error.message.length < NOTIFICATION_ERROR_MAX_LENGTH
            ? error.message
            : "Failed to delete preset script",
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return (
    <div className="wp-fade-in">
      {modal}
      <PageHeader
        title={preset.name}
        subtitle="Lua preset script"
        showBackButton
        backPath={PRESET_SCRIPTS_LIST_PATH}
        showBreadcrumbs={false}
        actions={[
          {
            label: "Edit",
            icon: <IconEdit size={16} />,
            onClick: () => navigate("./edit"),
            variant: "filled",
          },
          {
            label: "Delete",
            icon: <IconTrash size={16} />,
            onClick: handleDelete,
            color: "rose",
            variant: "light",
          },
        ]}
      />
      <Stack gap="lg">
        <Card withBorder p="lg" radius="md">
          <Title order={4} mb="xs">
            Description
          </Title>
          <Text c={preset.description ? undefined : "dimmed"}>
            {preset.description || "No description provided."}
          </Text>
          <Group gap="xl" mt="md">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Created
              </Text>
              <Text size="sm">{formatDate(preset.createdAt)}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Last updated
              </Text>
              <Text size="sm">{formatDate(preset.updatedAt)}</Text>
            </Stack>
          </Group>
        </Card>

        <Card withBorder p="lg" radius="md">
          <Group justify="space-between" mb="sm">
            <Title order={4}>Parameters</Title>
            <Badge variant="light" color="cyan">
              {parameters.length}
            </Badge>
          </Group>
          {parameters.length === 0 ? (
            <Text c="dimmed" size="sm">
              This preset has no declared parameters.
            </Text>
          ) : (
            <Table striped withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Required</Table.Th>
                  <Table.Th>Default</Table.Th>
                  <Table.Th>Description</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {parameters.map((parameter) => (
                  <Table.Tr key={parameter.name}>
                    <Table.Td>
                      <Code>{parameter.name}</Code>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={PARAMETER_TYPE_COLORS[parameter.type]}
                      >
                        {parameter.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {parameter.required ? (
                        <Badge color="rose" variant="light">
                          Required
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Optional
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {parameter.defaultValue ? (
                        <Code>{parameter.defaultValue}</Code>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        c={parameter.description ? undefined : "dimmed"}
                      >
                        {parameter.description || "—"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Card withBorder p="lg" radius="md">
          <Title order={4} mb="sm">
            Lua script
          </Title>
          <Paper
            withBorder
            p="md"
            radius="sm"
            style={(theme) => ({
              backgroundColor: theme.colors.dark[8],
              maxHeight: SCRIPT_PREVIEW_MAX_HEIGHT,
              overflow: "auto",
            })}
          >
            <Code
              block
              style={{ whiteSpace: "pre", fontSize: SCRIPT_PREVIEW_FONT_SIZE }}
            >
              {preset.script}
            </Code>
          </Paper>
        </Card>

        <Card withBorder p="lg" radius="md">
          <Title order={4} mb="sm">
            Validate
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Run static analysis on this Lua preset to catch syntax errors,
            undefined variables, and blocked functions.
          </Text>
          <ValidationPanel
            script={preset.script}
            context={ScriptContextType.Preset}
          />
        </Card>

        <Card withBorder p="lg" radius="md">
          <Title order={4} mb="sm">
            Simulate
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Execute this preset in the sandboxed runtime with the parameter
            values you choose. Inspect the collected effects, instructions
            used, and execution time before applying the preset to a
            tournament step.
          </Text>
          <SimulationPanel
            script={preset.script}
            context={ScriptContextType.Preset}
            parametersSchema={scriptParametersSchema}
          />
        </Card>
      </Stack>
    </div>
  );
};
